import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ExpandedState,
} from "@tanstack/react-table";
import * as React from "react";
import { useMemo, useState } from "react";
import type {
  ForecastClassBlock,
  RowData,
  TimeSlot,
} from "../../../models/weather/IWeatherForecast";
import { getTextAlignment } from "../../../utils/common/CommonHelperFunctions";
import { buildForecastData } from "../../../utils/weather/WeatherForecastHelper";
import { buildGroupedMetricColumns } from "./buildGroupedMetricColumns";
import { useStyles } from "./forecastTableStyles";

interface IGroupedMetricTableProps {
  timeSlots: TimeSlot[];
  payload: ForecastClassBlock[];
}

const GroupedMetricTable: React.FC<IGroupedMetricTableProps> = ({
  timeSlots,
  payload,
}) => {
  const classes = useStyles();

  const [expanded, setExpanded] = useState<ExpandedState>(true);

  // Build the hierarchical rows from props
  const data: RowData[] = useMemo(
    () => buildForecastData(timeSlots, payload),
    [timeSlots, payload]
  );

  // Columns using timeSlots and styling hooks
  const columns = useMemo(
    () =>
      buildGroupedMetricColumns(timeSlots, {
        toggleBtn: classes.toggleBtn,
        chevron: classes.chevron,
        chevronExpanded: classes.chevronExpanded,
        cellInner: classes.cellInner,
        cellModerate: classes.cellModerate,
        cellPoor: classes.cellPoor,
      }),
    [
      timeSlots,
      classes.toggleBtn,
      classes.chevron,
      classes.chevronExpanded,
      classes.cellInner,
      classes.cellModerate,
      classes.cellPoor,
    ]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(), // tree expansion
    getSubRows: (row) => row.subRows ?? [],
    state: { expanded },
    onExpandedChange: setExpanded,
    getRowId: (_row, index, parent) =>
      parent ? `${parent.id}.${index}` : `${index}`,
  });

  return (
    <div
      className={classes.scrollArea}
      role="region"
      aria-label="weather forecast scroll area"
    >
      <table
        className={classes.table}
        role="table"
        aria-label="weather forecast"
      >
        <thead>
          <tr>
            {table.getFlatHeaders().map((header) => (
              <th
                key={header.id}
                style={getTextAlignment(
                  (header.column.columnDef.meta as any)?.align
                )}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={getTextAlignment(
                    (cell.column.columnDef.meta as any)?.align
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GroupedMetricTable;
